import { isDebug } from "@base/BuildEnvironment";
import { IntersectionUtil } from "@base/math/IntersectionUtil";
import { Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { IPlane, Plane } from "@base/math/Plane";
import { IRay2, Ray2 } from "@base/math/Ray2";
import { Ray3 } from "@base/math/Ray3";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { IAxlePos, INetPositionOptions } from "@biz/definitions/MountDefinitons";
import { SST } from "@biz/definitions/SST";
import { ISunSailData, PoleId, PoleIndex, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { SunSailReducers } from "@biz/items/sunSail/SunSailReducers";

export class LogicS {
  static getFrontPointIndices(sunSailType: SunSailType, poleCount: number, sideIndex: number): PoleIndex[] {
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    return topology.groups[sideIndex].nonAxleIndices;
  }

  static getAxlePointIndexForFrontPole(frontPole: number): number {
    return (frontPole === PoleIndex.D || frontPole === PoleIndex.F) ? PoleIndex.A : PoleIndex.B;
  }

  static getSideIndexForPole(poleIndex: PoleIndex): number {
    return poleIndex >= PoleIndex.E ? 1 : 0;
  }

  static calcNetPositionsForGroup(sunSail: ISunSailData, groupIndex: number, options: INetPositionOptions | undefined): IVec3[] {
    const { sunSailType } = sunSail;

    const poleCount = Poles.getCount(sunSail);

    // SLogic.387
    const axleIndex = SunSailLogic.getAxleIndex(sunSail.sunSailType, groupIndex);

    if (sunSail.sunSailType === SunSailType.SolidayTwin && groupIndex === 1 && poleCount === 5) {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, axleIndex, options);
      const netPositions = SunSailLogic.calcNetPositionsFromMountPositions(sunSail, groupIndex, mountPositions);
      return netPositions;
    } else {
      const points: IVec3[] = [];

      const axleMount = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, SunSailLogic.getAxleIndex(sunSailType, groupIndex))!;
      const axleNet = SunSailLogic.calcAxleNetFromMountPositions(sunSail, groupIndex, axleMount.first, axleMount.second);
      points[0] = axleNet.first;
      points[1] = axleNet.second;

      const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
      const topGroup = topology.groups[groupIndex];
      const piC = topGroup.nonAxleIndices[0];
      const piD = topGroup.nonAxleIndices[1];

      const minDistOut = LogicS.calcDistanceOutForFront(sunSail, piC);
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, axleIndex, options);
      const frontLengthCD = LogicS.getFrontNetLength(sunSail, piC, axleMount);

      const { cd: centerCD } = LogicS.calcNetPointsOnABAndCD(Vec3.mid(axleNet.first, axleNet.second), minDistOut, piC, piD, mountPositions);
      const { cd: pConCD } = LogicS.calcNetPointsOnABAndCD(mountPositions[piC], minDistOut, piC, piD, mountPositions);
      const { cd: pDonCD } = LogicS.calcNetPointsOnABAndCD(mountPositions[piD], minDistOut, piC, piD, mountPositions);


      const npNetCDNormalized = Vec3.len2(pDonCD, pConCD) < 0.01 ? Vec3.dir(axleNet.first, axleNet.second) : Vec3.dir(pDonCD, pConCD);
      points[piC] = Vec3.addScaled(centerCD, npNetCDNormalized, frontLengthCD * 0.5)
      points[piD] = Vec3.addScaled(centerCD, npNetCDNormalized, -frontLengthCD * 0.5)

      if (Number.isNaN(frontLengthCD)) {
        throw new Error("frontLengthCD is NAN!")
      }
      if (Vec3.isNaN(npNetCDNormalized)) {
        throw new Error("Vector npNetCDNormalized is NAN!")
      }
      return points;
    }
  }

  /** @deprecated */
  static calcNetPositionGroups(sunSail: ISunSailData, options?: INetPositionOptions): IVec3[][] {
    const { sunSailType } = sunSail;

    // SLogic.387
    const groups: IVec3[][] = [];
    const poleCount = Poles.getCount(sunSail);
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    for (let i = 0; i < topology.groups.length; i++) {
      groups.push(LogicS.calcNetPositionsForGroup(sunSail, i, options));
    }
    return groups;
  }

  static calcGroundABNormalized(a: IVec3, b: IVec3): IVec3 {
    return Vec3.normalized(Vec3.xz(Vec3.to(a, b)));
  }

  static calcNetPointsOnABAndCD(p: IVec3, minDistanceOut: number, c: PoleIndex, d: PoleIndex, mountPositions: IVec3[]): {
    ab: IVec3;
    cd: IVec3;
  } {
    const groundABNormalized = LogicS.calcGroundABNormalized(mountPositions[PoleIndex.A], mountPositions[PoleIndex.B]);
    const plane = Plane.fromVector(groundABNormalized, Vec3.dot(groundABNormalized, p));

    const topCDRay = Vec3.len2(mountPositions[c], mountPositions[d]) < 0.01 ? Ray3.fromDir(mountPositions[c], Vec3.dir(mountPositions[PoleIndex.A], mountPositions[PoleIndex.B])) : Ray3.fromPoints(mountPositions[c], mountPositions[d]);
    const intersectionCD = IntersectionUtil.intersect(topCDRay, plane, true);
    if (!intersectionCD) {
      throw new Error("no intersection!");
    }

    const topABRay = Ray3.fromPoints(mountPositions[0], mountPositions[1]);
    const ab = IntersectionUtil.intersect(topABRay, plane, true);
    if (!ab) {
      throw new Error("no intersection!");
    }

    const axleToCD = Vec3.to(ab, intersectionCD);
    const axleToCDUnit = Vec3.normalized(axleToCD);
    const cd = Vec3.addScaled(ab, axleToCDUnit, minDistanceOut);


    if (isDebug()) {
      if (Vec3.isNaN(ab)) {
        throw new Error("Vector ab is NAN!")
      }
      if (Vec3.isNaN(cd)) {
        throw new Error("Vector cd is NAN!")
      }
    }

    return {
      ab,
      cd
    }
  }

  static calcMinDistOutForFront(sunSail: ISunSailData, frontPole: PoleIndex): number {
    const sideIndex = LogicS.getSideIndexForPole(frontPole);
    const indices = LogicS.getFrontPointIndices(sunSail.sunSailType, Poles.getCount(sunSail), sideIndex);

    let distMin = Number.MAX_VALUE;
    for (const i of indices) {
      const dist = SunSailLogic.calcDistanceOut(sunSail, i);
      distMin = Math.min(distMin, dist);
    }
    return distMin;
  }

  static calcDistanceOutForFront(sunSail: ISunSailData, frontPole: PoleIndex): number {

    const distOut = LogicS.calcMinDistOutForFront(sunSail, frontPole);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, frontPole);

    const maxAllowedDistanceOut = SunSailLogic.getMaxAllowedDistOut(sunSail.sunSailType, pylonType);
    return Math.min(distOut, maxAllowedDistanceOut);
  }

  static getFrontNetLength(sunSail: ISunSailData, frontPole: PoleIndex, axlePos: IAxlePos<IVec3>): number {
    const side = LogicS.getSideIndexForPole(frontPole);
    const minDistanceOut = LogicS.calcDistanceOutForFront(sunSail, frontPole);
    const netAxleLength = SunSailLogic.getAxleNetLength(sunSail, side, axlePos);
    const narrowFactor = SunSailLogic.getStaticNarrowFactor(sunSail.sunSailType);
    const frontLength = netAxleLength - narrowFactor * minDistanceOut * 2;
    return frontLength;
  }

  static calcFrontNetPosition(sunSail: ISunSailData, poleIndex: PoleIndex, distance: number, axlePos: IAxlePos<IVec3>, coilOutFactor: number = 1): IVec3 {
    // SLogic 127
    const sideIndex = LogicS.getSideIndexForPole(poleIndex);
    const [piC, piD] = LogicS.getFrontPointIndices(sunSail.sunSailType, Poles.getCount(sunSail), sideIndex);
    if (piD === undefined || piD >= Poles.getCount(sunSail)) {
      throw new Error("piD is undefined!");
    }

    const netPositions = LogicS.calcNetPositionsForGroup(sunSail, sideIndex, undefined);
    const cd = Vec3.len2(netPositions[piC], netPositions[piD]) < 0.01 ? Vec3.to(netPositions[0], netPositions[1]) : Vec3.to(netPositions[piC], netPositions[piD]);
    const cdNormalized = Vec3.normalized(cd);
    const fnp = (poleIndex === PoleIndex.C || poleIndex === PoleIndex.E)
      ? Vec3.addScaled(netPositions[piC], cdNormalized, distance)
      : Vec3.addScaled(netPositions[piD], cdNormalized, -distance);


    MathUtil.checkNaN([fnp, cdNormalized], "calcFrontNetPosition")
    if (coilOutFactor <= 0.999) {
      // SLogic 145 (different implementation here)
      const ap = SunSailMath.calcNearestAxlePoint(axlePos.first, axlePos.second, fnp)
      return Vec3.addScaled(ap, Vec3.to(ap, fnp), coilOutFactor);
    }
    return fnp;
  }

  static calcRopeDistFromNet_XS(sunSail: ISunSailData, poleId: PoleId, mountPositions: IVec3[]): number {
    const axlePoleIndex = poleId;
    const otherPoleIndex = poleId === PoleIndex.A ? PoleIndex.D : PoleIndex.C;
    const axlePos = Vec2.fromXZ(mountPositions[axlePoleIndex])
    const otherPos = Vec2.fromXZ(mountPositions[otherPoleIndex])
    const mountAxlePos = SunSailLogic.getAxlePos(sunSail.sunSailType, mountPositions);
    const netPos = Vec2.fromXZ(LogicS.calcFrontNetPosition(sunSail, otherPoleIndex, -0.05, mountAxlePos))

    const ab = Vec2.to(axlePos, otherPos);
    const normal = Vec2.normalized(Vec2.normalVector(ab));
    const d = Vec2.dot(normal, netPos) - Vec2.dot(normal, axlePos);
    return poleId === PoleIndex.A ? -d : d;
  }

  static #calcPoleDelta(sunSail: ISunSailData, poleIndex: number): IVec3 {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const delta = SunSailLogic.calcDeltaForLengthPos(sunSail, poleIndex, SunSailLogic.calcTopMountY(sunSail, poleIndex), SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex), SunSailLogic.calcPoleOffsetY(sunSail, poleIndex), PoleHelper.getTopOptions(poleLength));
    return delta;
  }

  static calcAxleGroundOrientation(sunSail: ISunSailData): IVec3 {
    const { sunSailType, poles } = sunSail;
    if (sunSailType === SunSailType.SolidayTwin) {
      return LogicS.calcGroundABNormalized(poles[0].position, poles[1].position);
    }

    const abGround = Vec3.xz(Vec3.to(poles[0].position, poles[1].position));
    const dist = (SunSailLogic.isPerpendicular(sunSail, 0) ? LogicS.#calcPoleDelta(sunSail, 0).z : 0) - (SunSailLogic.isPerpendicular(sunSail, 1) ? LogicS.#calcPoleDelta(sunSail, 1).z : 0);
    const value = dist / Vec3.len(abGround);
    const angle = -Math.atan(value);
    const rotated = Vec3.rotateY(abGround, angle);
    const n = Vec3.normalized(rotated);
    return n;
  }

  static calcAxlePoleOrientationVec(_sunSailId: string, poleIndex: number, sunSail: ISunSailData): IVec3 {
    const { sunSailType } = sunSail;
    const isAxle = PoleLogic.isAxle(sunSailType, poleIndex)
    if (!isAxle)
      throw new Error("Pole is not an axle!")

    const axleDir = LogicS.calcAxleGroundOrientation(sunSail);
    if (SunSailLogic.isPerpendicular(sunSail, poleIndex))
      return Vec3.from(-axleDir.z, 0, axleDir.x);
    else
      return poleIndex === 0 ? axleDir : Vec3.negate(axleDir);
  }

  static calcPoleOrientationVec(sunSailId: string, poleIndex: number, sunSail: ISunSailData): IVec3 {
    // SunSailData 509 (GetTopPoleVector)
    const { sunSailType, poles } = sunSail;
    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      return LogicS.calcAxlePoleOrientationVec(sunSailId, poleIndex, sunSail);
    }

    const axleVec = LogicS.calcAxleGroundOrientation(sunSail);

    if (SunSailLogic.isPolyPositioningForPole(sunSail, poleIndex)) {
      const center = Vec3.mid(poles[0].position, poles[1].position);
      return Vec3.normalized(Vec3.xz(Vec3.sub(center, poles[poleIndex].position)));
    } else {
      return poleIndex < PoleIndex.E ? Vec3.from(axleVec.z, 0, -axleVec.x) : Vec3.from(-axleVec.z, 0, axleVec.x)
    }
  }

  static calcGroundRotation(sunSailId: string, poleIndex: number, sunSail: ISunSailData): number {
    const orientation = LogicS.calcPoleOrientationVec(sunSailId, poleIndex, sunSail);
    return SunSailMath.calcGroundRotationFromVec(orientation);
  }

  static calcMountPositions(sunSail: ISunSailData, axleIndex: number, options?: INetPositionOptions): IVec3[] {
    const { id: sunSailId, sunSailType, positionMode } = sunSail;
    const poleCount = Poles.getCount(sunSail);
    switch (positionMode || "bottom") {
      case "mount": return sunSail.poles.map((_p, i) => SunSailLogic.getPoleMountPositionModePos(sunSail, i, axleIndex))
      case "bottom": {
        const mount: IVec3[] = [];
        const indices = PoleLogic.getAxleIndices(sunSailType);
        if (indices) {
          mount[indices.first] = SunSailLogic.calcMountPositionForAxlePole(sunSail, indices.first, axleIndex);
          mount[indices.second] = SunSailLogic.calcMountPositionForAxlePole(sunSail, indices.second, axleIndex);
        }

        for (let i = 0; i < poleCount; i++) {
          const pole = sunSail.poles[i];
          if (PoleLogic.isAxle(sunSailType, i)) {
            // ...already handled
          } else {
            const mountPosDelta = SunSailLogic.calcMountPosDelta(sunSail, i);
            const lengthBVal = SunSailLogic.getPoleLengthB(sunSail, i);
            const topMountY = SunSailLogic.calcTopMountY(sunSail, i);
            const poleAngle = SunSailLogic.getPoleAngle(sunSail, i);
            const poleLength = SunSailLogic.getPoleLength(sunSail, i);
            const heightChangeOffset = options?.heightChangeOffsets === undefined ? 0 : (Array.isArray(options?.heightChangeOffsets) ? options?.heightChangeOffsets[i] : options?.heightChangeOffsets);
            const m = PoleHelper.calcMatrixForLengthPos(sunSailId, i, poleLength, poleAngle, lengthBVal, SunSailLogic.calcBottomPoleExtension(sunSail, i), SunSailLogic.calcPoleOffsetY(sunSail, i), poleLength - topMountY + mountPosDelta.y - heightChangeOffset);

            const groundVec = LogicS.calcPoleOrientationVec(sunSailId, i, sunSail);
            const rotation = Math.atan2(-groundVec.x, -groundVec.z);
            const groundMatrix = SunSailLogic.calcPoleGroundMatrixFromValues(pole.position, rotation);
            const mountPos = Vec3.applyMatrix4(Vec3.xz(mountPosDelta), Mat4.mul(groundMatrix, m));
            mount[i] = mountPos;
          }
        }
        return mount;
      }
      default: throw new Error("LogicS.calcMountPosition: PositionMode not supported so far: " + positionMode)
    }
  }

  static isTrianglePole(_sunSailType: SunSailType, poleCount: number, poleIndex: number): boolean {
    return (poleCount === 3 || poleCount === 5) && (poleIndex === poleCount - 1);
  }

  static isQuadFrontPole(sunSailType: SunSailType, poleCount: number, poleId: PoleId): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;

    const ret = SST.is(sunSailType, SST.CsXsTwin) && poleId >= 2 && !LogicS.isTrianglePole(sunSailType, poleCount, poleId);
    return ret;
  }

  static setPolePosition(sunSail: ISunSailData, poleIndex: PoleIndex, newPosition: IVec3): ISunSailData {
    const { sunSailType } = sunSail;

    if (LogicS.isTrianglePole(sunSailType, Poles.getCount(sunSail), poleIndex))
      return SunSailReducers.setPolePositionDefault(sunSail, poleIndex, newPosition);


    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      return LogicS.setAxlePolePosition(sunSail, poleIndex, newPosition)
    } else {
      sunSail = LogicS.setFrontPolePosition(sunSail, SunSailLogic.calcMountPositionsForAxlePoles(sunSail, 0)!, poleIndex, newPosition);
      return LogicS.fixFrontPolePositions(sunSail, poleIndex);
    }
    // return SunSailReducers.setPolePositionDirect(sunSail, poleIndex, newPos);
  }

  static setAxlePolePosition(oldSunSail: ISunSailData, poleIndex: PoleIndex, newPosition: IVec3): ISunSailData {
    const oldGround: IVec3[] = [];
    for (let i = 0; i < Poles.getCount(oldSunSail); i++) {
      oldGround.push(Vec3.xz(oldSunSail.poles[i].position));
    }

    const old = {
      a: oldGround[0],
      b: oldGround[1],
      len: Vec3.len(oldGround[0], oldGround[1]),
      ray: Ray3.fromDir(oldGround[0], Vec3.dir(oldGround[0], oldGround[1]))
    }

    let newSunSail = SunSailLogic.applyAxleSize(oldSunSail, poleIndex, newPosition);
    const newAxlePos = { first: newSunSail.poles[0].position, second: newSunSail.poles[1].position }; // SunSailLogic.calcAxlePositions(newSunSail, 0);
    if (!newAxlePos)
      throw new Error("setFrontPolePosition - axle required!")
    // console.log("OldAxlePos", oldGround[0].x, oldGround[1].x);
    // console.log("NewAxlePos", newAxlePos.from.x, newAxlePos.to.x);

    const newA = Vec3.xz(newAxlePos.first);
    const newB = Vec3.xz(newAxlePos.second);
    const newAB = Vec3.to(newA, newB);
    const n = {
      a: newA,
      b: newB,
      ab: newAB,
      normal: Vec3.normalized(Vec3.from(-newAB.z, 0, newAB.x)),
    }

    if (Vec3.isNaN(n.normal)) {
      console.log("NAN!");
      return oldSunSail;
    }


    for (let i = 2; i < Poles.getCount(oldSunSail); i++) {
      const oldGroundRayPoint = Ray3.calcClosestPointOnRay(old.ray, oldGround[i])
      const distFactorX = Vec3.len(old.a, oldGroundRayPoint.point) / old.len;
      const distY = Vec3.len(oldGround[i], oldGroundRayPoint.point);

      const normal = i < PoleIndex.E ? n.normal : Vec3.negate(n.normal);
      const newPos = Vec3.addScaled(Vec3.addScaled(n.a, n.ab, distFactorX), normal, distY);
      newPos.y = oldSunSail.poles[i].position.y;
      // console.log("Dist: ", i, distFactorX, distY, newPos, oldGround[i])
      // newSunSail = this.setFrontPolePosition(newSunSail, newAxlePos, i, newPos);
      newSunSail = SunSailReducers.setPolePosition(newSunSail, i, newPos, { direct: true });
    }
    newSunSail = LogicS.fixFrontPolePositions(newSunSail);
    return newSunSail;
  }

  static setFrontPolePosition(sunSail: ISunSailData, axlePos: IAxlePos<IVec3>, poleIndex: number, p?: IVec3): ISunSailData {
    if (LogicS.isTrianglePole(sunSail.sunSailType, Poles.getCount(sunSail), poleIndex))
      return SunSailReducers.setPolePosition(sunSail, poleIndex, p || sunSail.poles[poleIndex].position, { direct: true });

    const newPos = LogicS.calcFrontPolePosition(sunSail, poleIndex, p || sunSail.poles[poleIndex].position, axlePos)
    return SunSailReducers.setPolePosition(sunSail, poleIndex, newPos, { direct: true });
  }

  static fixFrontPolePositions(sunSail: ISunSailData, except?: PoleIndex): ISunSailData {
    const axle = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, 0)!;
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      if (!PoleLogic.isAxle(sunSail.sunSailType, i) && except !== i)
        sunSail = LogicS.setFrontPolePosition(sunSail, axle, i);
    }
    return sunSail;
  }

  public static calcCollisionPlanes(region: IVec2[], poleIndex: PoleIndex, mountAxle: IAxlePos<IVec3>): IPlane[] {
    const planes: IPlane[] = [];

    {
      const ab = Vec2.fromXZ(Vec3.to(mountAxle.first, mountAxle.second));
      const n = Vec2.normalized(Vec2.normalVector(ab, poleIndex === PoleIndex.C || poleIndex === PoleIndex.D));
      const p = Vec2.addScaled(Vec2.fromXZ(mountAxle.first), n, -1.5);
      planes.push(Plane.from(n.x, 0, n.y, Vec2.dot(n, p)));
    }


    let lastPlane: IPlane | undefined;
    for (let i = 1; i < region.length; i++) {
      const a = region[i];
      const b = region[(i + 1) % region.length];

      const ab = Vec2.to(a, b);
      const n = Vec2.normalized(Vec2.normalVector(ab, poleIndex === PoleIndex.D || poleIndex === PoleIndex.E));
      const d = Vec2.dot(n, a);

      const plane = Plane.from(n.x, 0, n.y, d);
      if (lastPlane) {
        const dot = Vec3.dot(lastPlane.n, plane.n);
        if (dot < 0) {
          const lastN = Vec3.from(lastPlane.n.z, 0, -lastPlane.n.x);
          const projected = Vec3.project(plane.n, lastN);
          const n2 = Vec3.normalized(projected);

          // const n2 = Vec3.normalized(Vec3.mid(lastPlane.n, plane.n));
          planes.push(Plane.from(n2.x, n2.y, n2.z, Vec3.dot(n2, Vec3.fromXY(a))))
        }
      }

      planes.push(plane);

      lastPlane = plane;
    }
    return planes;
  }

  static calcFrontPolePosition(sunSail: ISunSailData, poleIndex: PoleIndex, newPosition: IVec3, axlePos: IAxlePos<IVec3>): IVec3 {
    if (SunSailLogic.isPolyPositioningForPole(sunSail, poleIndex)) {
      const allowedRegion = LogicS.calcAllowedRegion(sunSail, poleIndex);
      if (allowedRegion) {
        let newPos2 = Vec3.xz(newPosition);
        const collisionPlanes = LogicS.calcCollisionPlanes(allowedRegion.region, poleIndex, axlePos);
        for (const p of collisionPlanes) {
          const dist = Plane.calcDistanceToPlane(p, newPos2)
          if (dist > 0) {
            newPos2 = Vec3.addScaled(newPos2, p.n, -dist);
          }
        }
        return Vec3.from(newPos2.x, newPosition.y, newPos2.z)
      }
      // const p = LogicS.calcNewFrontPositionOnLine(sunSail, poleIndex, newPosition, mountPositions);
      // return Vec3.from(
      //   p.x,
      //   newPosition.y,
      //   p.z
      // )
      return newPosition;
    } else {
      const p = LogicS.calcNewFrontPositionOnLine(sunSail, poleIndex, newPosition, axlePos);
      return Vec3.from(
        p.x,
        newPosition.y,
        p.z
      )
    }
  }

  static calcNewFrontPositionOnLine(sunSail: ISunSailData, frontPole: PoleIndex, newPos: IVec3, axlePos: IAxlePos<IVec3>): IVec3 {
    const rayAB = LogicS.calcGroundRayAB(axlePos);
    const frontMountDist = LogicS.getMountDistance(sunSail, frontPole, axlePos)

    const frontTopD = LogicS.calcFrontNetPosition(sunSail, frontPole, SunSailLogic.getKnotOuterDist() + frontMountDist * 0.5, axlePos);
    const forwardVector = LogicS.calcForwardVector(axlePos, frontPole >= PoleIndex.E);

    const rayForward = Ray2.fromDir(Vec2.fromXZ(frontTopD), Vec2.fromXZ(forwardVector));
    const groundACoorrected2 = IntersectionUtil.intersectRay2(rayAB, rayForward)
    if (!groundACoorrected2)
      return newPos;
    const groundACorrected = Vec3.from(groundACoorrected2.x, 0, groundACoorrected2.y);
    const forwardVector2 = Vec3.project(Vec3.to(groundACorrected, newPos), forwardVector);

    // don't allow positioning of front pole nearer than minDist or behind axle...
    const minDist = 1.5;
    if (Vec3.dot(forwardVector2, forwardVector) < 0 || Vec3.len(forwardVector2) < minDist)
      return Vec3.addScaled(groundACorrected, forwardVector, minDist)
    return Vec3.add(groundACorrected, forwardVector2);
  }

  static getMountDistance(sunSail: ISunSailData, frontPole: PoleIndex, axlePos: IAxlePos<IVec3>): number {
    return LogicS.getMountDistanceDirect(LogicS.getFrontNetLength(sunSail, frontPole, axlePos))
  }

  static getMountDistanceDirect(frontNetLength: number): number {
    return (frontNetLength - SunSailLogic.getKnotOuterDist() * 2.0) / 3.0;
  }

  static calcForwardVector(axlePos: IAxlePos<IVec3>, negative?: boolean): IVec3 {
    const groundAB = LogicS.calcGroundABNormalized(axlePos.first, axlePos.second);
    if (negative)
      return Vec3.from(groundAB.z, 0, -groundAB.x);
    else
      return Vec3.from(-groundAB.z, 0, groundAB.x);
  }

  static calcGroundRayAB(axlePos: IAxlePos<IVec3>): IRay2 {
    const groundA = Vec2.fromXZ(axlePos.first);
    const groundB = Vec2.fromXZ(axlePos.second);
    const ab = Vec2.dir(groundA, groundB);
    return Ray2.fromDir(groundA, ab);
  }

  static calcAllowedRegion(sunSail: ISunSailData, poleIndex: PoleIndex): { region: IVec2[], splitter: IVec2[] } {
    const { sunSailType } = sunSail;
    // SLogic 227
    const sideIndex = LogicS.getSideIndexForPole(poleIndex);
    const axleMountPos = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, SunSailLogic.getAxleIndex(sunSail.sunSailType, sideIndex))!;

    const md = LogicS.getMountDistance(sunSail, poleIndex, axleMountPos);
    const knotOuterDistance = SunSailLogic.getKnotOuterDist();
    // const groundN = Vec3.xz(LogicS.calcFrontNetPosition(sunSail, poleIndex, knotOuterDistance, mountPositions, 0));
    const groundABNormalized = LogicS.calcGroundABNormalized(axleMountPos.first, axleMountPos.second);
    let groundNormal = Vec3.from(-groundABNormalized.z, 0, groundABNormalized.x);
    if (poleIndex >= PoleIndex.E)
      groundNormal = Vec3.negate(groundNormal);

    const maxDistIn = SunSailLogic.getMaxAllowedDistInNonAxle(sunSailType);
    const minDistIn = SunSailLogic.getMinAllowedDistIn(sunSail, poleIndex)

    const distFromNormal = SunSailLogic.calcAreaPositioningDistFromNormal(sunSailType);
    const groundInner = Vec3.xz(LogicS.calcFrontNetPosition(sunSail, poleIndex, knotOuterDistance + md * 0.5 - distFromNormal, axleMountPos));
    const frontInner = Vec3.addScaled(groundInner, groundNormal, minDistIn)
    const backInner = Vec3.addScaled(groundInner, groundNormal, maxDistIn);


    if (SST.is(sunSailType, SST.CsTwin)) {
      const diag2 = LogicS.calcDiagVector(-68.0, groundNormal, poleIndex);
      const minDistOut = LogicS.calcDistanceOutForFront(sunSail, poleIndex);

      const netPositions = SunSailLogic.calcNetPositions(sunSail, sideIndex, {});
      let inflectionPoint: IVec3;
      {
        const [piC, piD] = LogicS.getFrontPointIndices(sunSail.sunSailType, Poles.getCount(sunSail), sideIndex);
        const axlePointIndex = LogicS.getAxlePointIndexForFrontPole(poleIndex);
        const axlePoint = netPositions[axlePointIndex];
        const mountPositions = SunSailLogic.calcMountPositions(sunSail, SunSailLogic.getAxleIndex(sunSail.sunSailType, sideIndex), undefined);
        const { cd } = LogicS.calcNetPointsOnABAndCD(axlePoint, minDistOut, piC, piD, mountPositions);
        inflectionPoint = Vec3.addScaled(Vec3.xz(cd), groundNormal, 1.5);
      }

      const groundL6 = Vec3.xz(LogicS.calcFrontNetPosition(sunSail, poleIndex, knotOuterDistance + md * 0.5, axleMountPos));
      const d = Vec3.dir(groundL6, inflectionPoint)
      const frontOuter = Vec3.addScaled(groundL6, d, minDistIn / Vec3.dot(d, groundNormal));
      //const backOuter = Vec3.addScaled(inflectionPoint, diag2, 1 + (maxDistIn - 1.5) * 1 / Vec3.dot(diag2, groundNormal));
      const backOuter = Vec3.addScaled(Vec3.xz(netPositions[poleIndex]), diag2, (maxDistIn * 1) / Vec3.dot(diag2, groundNormal));

      const frontMid = Vec3.mid(frontOuter, frontInner)
      const backMid = Vec3.mid(backInner, backOuter);
      const fb = Vec3.dir(frontMid, backMid);

      const frontSplitter = Vec3.addScaled(frontMid, fb, -1);
      const backSplitter = Vec3.addScaled(backMid, fb, 1);
      const positions = [
        frontOuter,
        frontInner,
        backInner,
        backOuter,
        inflectionPoint,
      ]
      const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
      const delta = SunSailLogic.calcDeltaForLengthPos(sunSail, poleIndex, SunSailLogic.calcTopMountY(sunSail, poleIndex), SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex), SunSailLogic.calcPoleOffsetY(sunSail, poleIndex), PoleHelper.getTopOptions(poleLength));
      const deltaDir = Vec3.mul(groundNormal, -delta.z);
      const area: IVec2[] = positions.map((v) => Vec2.from(v.x + deltaDir.x, v.z + deltaDir.z));
      return {
        region: area,
        splitter: [frontSplitter, backSplitter].map((v) => Vec2.from(v.x + deltaDir.x, v.z + deltaDir.z))
      }
    } else {
      const netPositions = SunSailLogic.calcNetPositions(sunSail, sideIndex, {});
      const groundNet = Vec3.xz(netPositions[poleIndex]);
      const diag2 = LogicS.calcDiagVector(-60, groundNormal, poleIndex);

      // const v = Vec3.mul(diag2, 1 / Vec3.dot(diag2, groundNormal));

      const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
      const delta = SunSailLogic.calcDeltaForLengthPos(sunSail, poleIndex, SunSailLogic.calcTopMountY(sunSail, poleIndex), SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex), SunSailLogic.calcPoleOffsetY(sunSail, poleIndex), PoleHelper.getTopOptions(poleLength));
      const deltaDir = Vec3.mul(groundNormal, -delta.z);

      return {
        region: [
          Vec3.addScaled(groundNet, diag2, (minDistIn) / Vec3.dot(diag2, groundNormal)),
          frontInner,
          backInner,
          Vec3.addScaled(groundNet, diag2, (maxDistIn) / Vec3.dot(diag2, groundNormal)),
        ].map((v) => Vec2.from(v.x + deltaDir.x, v.z + deltaDir.z)),
        splitter: []
      }
    }
  }

  private static calcDiagVector(diagAngle: number, groundNormal: IVec3, poleIndex: PoleIndex): IVec3 {
    if (poleIndex >= PoleIndex.E)
      diagAngle = -diagAngle;

    const angle = MathUtil.deg2rad(90 - diagAngle);
    const unitDiag = 1 / Math.cos(angle);
    const diag2 = Vec3.rotateY(groundNormal, (poleIndex === PoleIndex.D || poleIndex === PoleIndex.F) ? angle : -angle);
    const diag3 = Vec3.mul(diag2, unitDiag);
    return Vec3.normalized(diag3);
  }

  static getFrontNetLengthForNetPositions(netPositions: IVec3[], groupIndex: number): number {
    if (groupIndex === 0 && netPositions.length >= 4) {
      return Vec3.len(netPositions[2], netPositions[3]);
    } else if (netPositions.length >= 6)
      return Vec3.len(netPositions[4], netPositions[5]);
    return 0.0;
  }
}